import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { getHistory } from '../../utils/api';
import { useApp } from '../../context/AppContext';
import { useConsumable as consumeSupply } from '../../utils/api';
import { parseLocalDate, fmtDuration, isOverdue } from '../../utils/dates';

const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const fmtScheduledMonths = (months) =>
  months?.length ? [...months].sort((a, b) => a - b).map((m) => MONTH_ABBR[m - 1]).join(' · ') : null;

const FREQ_LABELS = {
  weekly: 'Every week', monthly: 'Every month', quarterly: 'Every 3 months',
  biannual: 'Every 6 months', annual: 'Every year', custom: 'Custom',
};

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const TaskDetailModal = ({ task, onClose, onEdit }) => {
  const { markTaskComplete, removeTask, appliances } = useApp();
  const linkedAppliance = task.linkedAppliance
    ? appliances.find((a) => a._id === task.linkedAppliance)
    : null;

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [completedDate, setCompletedDate] = useState(todayStr());
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [supplyPrompt, setSupplyPrompt] = useState([]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    setHistoryLoading(true);
    getHistory({ taskId: task._id })
      .then(({ data }) => setHistory(data.slice(0, 10)))
      .catch(() => {})
      .finally(() => setHistoryLoading(false));
  }, [task._id]);

  const handleConfirm = async () => {
    setSubmitting(true);
    setConfirming(false);
    try {
      const linked = await markTaskComplete(task._id, { completedAt: completedDate, notes: notes || undefined });
      if (linked.length > 0) {
        setSupplyPrompt(linked);
      } else {
        onClose();
      }
    } catch {
      setConfirming(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSupplyUse = async (id) => {
    await consumeSupply(id, 1);
    const remaining = supplyPrompt.filter((s) => s._id !== id);
    setSupplyPrompt(remaining);
    if (remaining.length === 0) onClose();
  };

  const handleSupplySkip = (id) => {
    const remaining = supplyPrompt.filter((s) => s._id !== id);
    setSupplyPrompt(remaining);
    if (remaining.length === 0) onClose();
  };

  const due = parseLocalDate(task.nextDue);
  const overdue = isOverdue(due);
  const duration = fmtDuration(task.estimatedMinutes);
  const schedLabel = fmtScheduledMonths(task.scheduledMonths);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-detail" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{task.title}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-form">
          {/* Badges */}
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
            <span className={`badge badge-${task.category}`}>{task.category}</span>
            <span className="badge">{schedLabel ?? FREQ_LABELS[task.frequency] ?? task.frequency}</span>
          </div>

          {/* Key details */}
          <dl className="detail-list">
            <dt>Next Due</dt>
            <dd style={{ color: overdue ? 'var(--danger)' : 'inherit', fontWeight: overdue ? 700 : 'inherit' }}>
              {format(due, 'MMMM d, yyyy')}{overdue ? ' — overdue' : ''}
            </dd>
            {task.lastCompleted && (
              <><dt>Last Done</dt><dd>{format(parseLocalDate(task.lastCompleted), 'MMMM d, yyyy')}</dd></>
            )}
            {duration && <><dt>Est. Time</dt><dd>{duration}</dd></>}
            {task.professionalCost != null && (
              <><dt>Pro Cost</dt><dd>${Number(task.professionalCost).toLocaleString()}</dd></>
            )}
            {linkedAppliance && <><dt>Appliance</dt><dd>{linkedAppliance.name}</dd></>}
            {schedLabel && <><dt>Months</dt><dd>{schedLabel}</dd></>}
          </dl>

          {(task.description || task.notes) && (
            <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {task.description && <p style={{ fontSize: '0.875rem', color: 'var(--muted)', lineHeight: 1.6, margin: 0 }}>{task.description}</p>}
              {task.notes && <p style={{ fontSize: '0.875rem', lineHeight: 1.6, margin: 0 }}>{task.notes}</p>}
            </div>
          )}

          {/* Completion flow */}
          {supplyPrompt.length > 0 ? (
            <div className="task-complete-form" style={{ marginTop: '1rem' }}>
              <p style={{ margin: '0 0 0.5rem', fontSize: '0.85rem', fontWeight: 600 }}>Did you use these supplies?</p>
              {supplyPrompt.map((s) => (
                <div key={s._id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' }}>
                  <span style={{ flex: 1, fontSize: '0.85rem' }}>
                    {s.name} <span style={{ color: '#94a3b8' }}>({s.quantityOnHand} on hand)</span>
                  </span>
                  <button className="btn btn-success btn-sm" onClick={() => handleSupplyUse(s._id)}>Used 1</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => handleSupplySkip(s._id)}>Skip</button>
                </div>
              ))}
            </div>
          ) : confirming ? (
            <div className="task-complete-form" style={{ marginTop: '1rem' }}>
              <label className="task-complete-label">
                Completed on
                <input
                  type="date"
                  className="task-complete-date"
                  value={completedDate}
                  max={todayStr()}
                  onChange={(e) => setCompletedDate(e.target.value)}
                  autoFocus
                />
              </label>
              <input
                className="task-complete-notes"
                placeholder="Notes (optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
              <div className="task-complete-actions">
                <button className="btn btn-success btn-sm" onClick={handleConfirm} disabled={submitting}>
                  {submitting ? '…' : 'Confirm'}
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => setConfirming(false)}>Cancel</button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: '0.6rem', marginTop: '1.25rem', flexWrap: 'wrap' }}>
              <button className="btn btn-success" onClick={() => { setCompletedDate(todayStr()); setNotes(''); setConfirming(true); }}>
                ✓ Mark Done
              </button>
              <button className="btn btn-secondary" onClick={() => { onClose(); onEdit(task); }}>Edit</button>
              <button className="btn btn-danger" onClick={() => { removeTask(task._id); onClose(); }}>Delete</button>
            </div>
          )}

          {/* Completion history */}
          <div style={{ marginTop: '1.5rem' }}>
            <h3 style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--muted)', marginBottom: '0.5rem' }}>
              Completion History
            </h3>
            {historyLoading ? (
              <p style={{ fontSize: '0.82rem', color: 'var(--muted)' }}>Loading…</p>
            ) : history.length === 0 ? (
              <p style={{ fontSize: '0.82rem', color: 'var(--muted)' }}>No completions recorded yet.</p>
            ) : (
              <div className="task-history-list">
                {history.map((h) => (
                  <div key={h._id} className="task-history-row">
                    <span className="task-history-date">{format(parseLocalDate(h.completedAt), 'MMM d, yyyy')}</span>
                    {h.notes && <span className="task-history-notes">{h.notes}</span>}
                    {h.cost != null && <span className="task-history-cost">${Number(h.cost).toFixed(2)}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskDetailModal;
